import { Component, OnInit, OnDestroy, inject, ViewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { WeatherService, WeatherData } from '../../../../core/services/weather.service';
import { FieldService } from '../../../../core/services/field.service';
import { CustomerService } from '../../../../core/services/customer.service';
import { ChemicalService, Chemical } from '../../../../core/services/chemical.service';
import { ChemicalTemplateService, ChemicalTemplate as ChemicalTemplateModel } from '../../../../core/services/chemical-template.service';
import { SprayRecordService, CreateSprayRecordDto } from '../../../../core/services/spray-record.service';
import { OrganizationService, OrganizationDrone, OrganizationMember } from '../../../../core/services/organization.service';
import { OrganizationContextService } from '../../../../core/services/organization-context.service';
import { CertificationService } from '../../../../core/services/certification.service';
import { Certification as CertificationModel } from '../../../../core/models/certification.model';
import { Field as FieldModel } from '../../../../core/models/field.model';
import { toDateInputValue } from '../../../../shared/utils/date.utils';
import {
  DroneFlightEntry,
  AppliedChemical as AppliedChemicalModel,
  FlightPathData,
  ChemicalUsage,
  calculateChemicalUsage,
  parseRate,
  parseRateToStructured,
  parseRateUnit,
  buildRateString,
  toOz,
  formatBestUnit,
  RATE_UNIT_OPTIONS,
  DEFAULT_RATE_UNIT,
  EQUIPMENT_MALFUNCTION_TYPES,
  ACCIDENT_TYPES,
  UA_LOSS_TYPES,
  PPE_OPTIONS,
  APPLICATION_TYPE_OPTIONS
} from '../../../../core/models/spray-record.model';
import {
  buildDroneFlightPayload,
  normalizeNumFlights,
  DroneFlightFormData
} from './drone-flight-payload';
import { FieldQuickAddComponent } from '../../../../shared/components/field-quick-add/field-quick-add.component';
import { ChemicalTypeaheadComponent } from '../../../../shared/components/chemical-typeahead/chemical-typeahead.component';
import { FlightMapComponent } from '../../../../shared/components/flight-map/flight-map.component';
import { FlightMapService } from '../../../../core/services/flight-map.service';
import { UnitInputComponent } from '../../../../shared/components/unit-input/unit-input.component';
import { DISTANCE_UNITS, PRESSURE_UNITS, SPEED_UNITS, ALTITUDE_UNITS, VOLUME_UNITS, TIME_INTERVAL_UNITS, DEFAULT_UNITS } from '../../../../shared/constants/unit-options';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

interface Pilot {
  id: string;
  name: string;
  // Certification data for compliance snapshots
  part107Number?: string | null;
  part107Expiration?: string | null;
  stateApplicatorNumber?: string | null;
  stateApplicatorState?: string | null;
  stateApplicatorExpiration?: string | null;
}

interface Drone {
  id: string;
  name: string;
  serialNumber: string;
  nNumber?: string;
  ownership?: 'adn' | 'organization';
}

interface Field {
  id: string;
  name: string;
  acreage: number;
  cropType: string;
  latitude: number | null;
  longitude: number | null;
  address?: string;
  customerId?: string;
}

interface Certification {
  id: string;
  name: string;
  certNumber: string;
  state?: string;
  expirationDate: string;
}

// Local interface for applied chemicals (simplified for form use)
interface AppliedChemical {
  id: string;
  chemicalId?: string;
  name: string;
  epaNumber: string;
  rate: string;
  rateValue: number | null;
  rateUnit: string;  // compound unit like "oz/ac"
  source: 'book' | 'custom' | 'template';
  templateName?: string;
}

// Per-drone form shape + payload construction live in drone-flight-payload.ts
// so the absent/null/zero semantics can be unit-tested — component specs are
// not runnable in this app (jest testEnvironment: 'node', no jest-preset-angular).
export type { DroneFlightFormData } from './drone-flight-payload';

@Component({
  selector: 'app-spray-record-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, NgxMaskDirective, FieldQuickAddComponent, ChemicalTypeaheadComponent, FlightMapComponent, UnitInputComponent, MaxLengthDirective, CharCounterDirective],
  templateUrl: './spray-record-form.component.html',
  styleUrls: ['./spray-record-form.component.scss']
})
export class SprayRecordFormComponent implements OnInit, OnDestroy {
  @ViewChild('fieldQuickAdd') fieldQuickAdd!: FieldQuickAddComponent;
  @ViewChild('chemicalTypeahead') chemicalTypeahead?: ChemicalTypeaheadComponent;

  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);
  private chemicalService = inject(ChemicalService);
  private organizationService = inject(OrganizationService);

  // Expose Math for template
  Math = Math;

  isDragging = false;
  isProcessing = false;
  uploadError: string | null = null;
  form!: FormGroup;
  isEditMode = false;
  recordId: string | null = null;
  isLoading = false;
  isSaving = false;
  validationErrors: string[] = [];
  genericError: string = '';
  showDroneFlightErrors = false;

  /** Conflict message per drone flight row, keyed by flight id. */
  flightConflicts = new Map<string, string>();
  private conflictTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private conflictTokens = new Map<string, number>();

  // Chemical source selection
  chemicalSourceOptions = [
    { value: 'lookup', label: 'Chemical Book / Custom' },
    { value: 'template', label: 'Chemical Template' }
  ];

  // Selected chemical from typeahead
  selectedChemical: Chemical | null = null;

  // Chemical templates from API
  private chemicalTemplateService = inject(ChemicalTemplateService);
  chemicalTemplates: ChemicalTemplateModel[] = [];
  isLoadingTemplates = false;

  // Spray record service for API calls
  private sprayRecordService = inject(SprayRecordService);

  // Certification service for API calls
  private certificationService = inject(CertificationService);
  private organizationContext = inject(OrganizationContextService);

  // Loaded from API
  pilots: Pilot[] = [];
  drones: Drone[] = [];
  isLoadingDrones = false;
  isLoadingPilots = false;

  // Fields are cached from FieldService to avoid calling getter on every change detection
  fields: Field[] = [];

  // Customer name cache for field dropdown display
  private customerNameCache: Map<string, string> = new Map();

  // Certifications loaded from API
  certifications: Certification[] = [];
  isLoadingCertifications = false;

  // Organization FAA certifications (for compliance snapshots)
  // 44807 is the operating certification shown in the dropdown
  // Part 137 is also captured for compliance records
  orgFaa44807: { id: string | null; number: string | null; expiration: string | null } = { id: null, number: null, expiration: null };
  orgFaaPart137: { number: string | null; expiration: string | null } = { number: null, expiration: null };

  // Applied chemicals list (shared across all drones)
  appliedChemicals: AppliedChemical[] = [];

  // Drone flights array
  droneFlights: DroneFlightFormData[] = [];

  // Cached computed values (to avoid infinite change detection)
  selectedField: Field | undefined;
  selectedCustomer: { id: string; name: string } | undefined;
  selectedCertification: Certification | undefined;
  selectedTemplate: ChemicalTemplateModel | undefined;
  availableDronesMap: Map<string, Drone[]> = new Map();
  totalChemicalUsage: { chemicalName: string; totalAmount: string }[] = [];
  droneNameMap: Map<string, string> = new Map();

  // Weather lookup state
  private weatherService = inject(WeatherService);
  isLoadingWeather = false;
  weatherError: string | null = null;
  weatherLookupSuccess = false;

  // Flight map state
  private flightMapService = inject(FlightMapService);
  showFlightMapModal = false;
  flightMapModalFlight: DroneFlightFormData | null = null;
  showUploadOverlay = false;

  statuses = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  windDirections = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

  // Unit options for standardized inputs
  distanceUnits = DISTANCE_UNITS;
  pressureUnits = PRESSURE_UNITS;
  speedUnits = SPEED_UNITS;
  altitudeUnits = ALTITUDE_UNITS;
  volumeUnits = VOLUME_UNITS;
  timeIntervalUnits = TIME_INTERVAL_UNITS;
  defaultUnits = DEFAULT_UNITS;

  // Incident dropdown options (from CropFlight aerial-record)
  equipmentMalfunctionTypes = EQUIPMENT_MALFUNCTION_TYPES;
  accidentTypes = ACCIDENT_TYPES;
  uaLossTypes = UA_LOSS_TYPES;

  // Ground record options (from CropFlight ground-record)
  ppeOptions = PPE_OPTIONS;
  applicationTypeOptions = APPLICATION_TYPE_OPTIONS;

  // Rate unit options for structured rate input
  rateUnitOptions = RATE_UNIT_OPTIONS;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Reactively update fields when fieldOptions signal changes
    effect(() => {
      const fieldOptions = this.fieldService.fieldOptions();
      if (fieldOptions.length > 0) {
        this.fields = this.fieldService.getFieldOptions();
        this.buildCustomerNameCache();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Initialize form FIRST before any async operations
    this.initForm();

    // Initialize cached data
    this.fields = this.fieldService.getFieldOptions();
    this.buildCustomerNameCache();

    // Load data from API
    await this.loadDronesAndPilots();
    this.buildDroneNameMap();

    // Load certifications from API
    this.loadCertifications();

    // Load chemical templates from API
    this.loadChemicalTemplates();

    this.checkEditMode();

    // Add initial drone flight if not in edit mode
    if (!this.isEditMode && this.droneFlights.length === 0) {
      console.log('[SprayRecordForm] Adding initial drone flight in ngOnInit');
      this.addDroneFlight();
      console.log('[SprayRecordForm] After addDroneFlight, droneFlights.length:', this.droneFlights.length);
    }
  }

  ngOnDestroy(): void {
    this.conflictTimers.forEach(timer => clearTimeout(timer));
    this.conflictTimers.clear();
    this.conflictTokens.clear();
    this.flightConflicts.clear();
  }

  /**
   * Load drones and pilots from API
   * Uses the /pilots endpoint to only get pilots with valid Part 107 certifications
   */
  private async loadDronesAndPilots(): Promise<void> {
    this.isLoadingDrones = true;
    this.isLoadingPilots = true;

    try {
      // Load drones and pilots in parallel
      // Use getMyOrganizationPilots to only get pilots with valid Part 107 certs
      const [dronesResponse, pilotsResponse] = await Promise.all([
        this.organizationService.getMyOrganizationDrones().catch(err => {
          console.error('[SprayRecordForm] Error loading drones:', err);
          return [];
        }),
        this.organizationService.getMyOrganizationPilots().catch(err => {
          console.error('[SprayRecordForm] Error loading pilots:', err);
          return [];
        })
      ]);

      // Map API drones to component format
      this.drones = dronesResponse.map(drone => ({
        id: drone.id,
        name: drone.name,
        serialNumber: drone.serialNumber,
        nNumber: drone.nNumber,
        ownership: drone.ownership
      }));

      // Map API pilots to component format (includes certification data for snapshots)
      this.pilots = pilotsResponse.map(pilot => ({
        id: pilot.id,
        name: pilot.name,
        part107Number: pilot.part107Number,
        part107Expiration: pilot.part107Expiration,
        stateApplicatorNumber: pilot.stateApplicatorNumber,
        stateApplicatorState: pilot.stateApplicatorState,
        stateApplicatorExpiration: pilot.stateApplicatorExpiration
      }));

      console.log(`[SprayRecordForm] Loaded ${this.drones.length} drones and ${this.pilots.length} pilots (with valid Part 107)`);
    } catch (error) {
      console.error('[SprayRecordForm] Error loading drones/pilots:', error);
    } finally {
      this.isLoadingDrones = false;
      this.isLoadingPilots = false;
    }
  }

  private async loadCertifications(): Promise<void> {
    this.isLoadingCertifications = true;
    try {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        const certs = await this.certificationService.getOrganizationCertifications(orgId);

        // Extract organization FAA certifications for compliance snapshots
        // The 44807 is the OPERATING certification for spray records
        const faa44807 = certs.find(c => c.certificationType === 'faa-44807' && c.status === 'active');
        const faaPart137 = certs.find(c => c.certificationType === 'faa-part-137' && c.status === 'active');

        this.orgFaa44807 = {
          id: faa44807?.id || null,
          number: faa44807?.certificateNumber || null,
          expiration: faa44807?.expirationDate || null
        };
        this.orgFaaPart137 = {
          number: faaPart137?.certificateNumber || null,
          expiration: faaPart137?.expirationDate || null
        };

        // The certification dropdown now shows the 44807 as the operating certification
        // If the org has a valid 44807, show it as the only option
        if (faa44807) {
          this.certifications = [{
            id: faa44807.id,
            name: 'FAA 44807 Exemption',
            certNumber: faa44807.certificateNumber,
            state: '', // FAA certs are federal, not state-specific
            expirationDate: faa44807.expirationDate || ''
          }];

          // Auto-select the 44807 for NEW records only (not edit mode)
          // In edit mode, the saved certificationId will be loaded from the record
          if (!this.isEditMode) {
            this.form.patchValue({ certificationId: faa44807.id });
            this.selectedCertification = this.certifications[0];
          }
        } else {
          this.certifications = [];
        }
      }
    } catch (error) {
      console.error('Error loading certifications:', error);
      this.certifications = [];
    } finally {
      this.isLoadingCertifications = false;
    }
  }

  private getCertificationDisplayName(cert: CertificationModel): string {
    // Map certification type to readable name
    const typeNames: Record<string, string> = {
      'part_107': 'Part 107 Remote Pilot',
      'part_137': 'Part 137 Agricultural Operator',
      'state_pesticide': 'State Pesticide Applicator',
      'commercial_operators': 'Commercial Operators License',
      'other': 'Other Certification'
    };
    return typeNames[cert.certificationType] || cert.certificationType;
  }

  private async loadChemicalTemplates(): Promise<void> {
    this.isLoadingTemplates = true;
    try {
      await this.chemicalTemplateService.loadTemplates();
      this.chemicalTemplates = this.chemicalTemplateService.templates();
    } catch (error) {
      console.error('Error loading chemical templates:', error);
      this.chemicalTemplates = [];
    } finally {
      this.isLoadingTemplates = false;
    }
  }

  private buildDroneNameMap(): void {
    this.droneNameMap.clear();
    this.drones.forEach(d => this.droneNameMap.set(d.id, d.name));
  }

  private buildCustomerNameCache(): void {
    this.customerNameCache.clear();
    this.fields.forEach(f => {
      if (f.customerId) {
        const customer = this.customerService.getCustomerById(f.customerId);
        if (customer) {
          this.customerNameCache.set(f.customerId, customer.name);
        }
      }
    });
  }

  // Get display name for field dropdown (includes customer if available)
  getFieldDisplayName(field: Field): string {
    if (field.customerId) {
      const customerName = this.customerNameCache.get(field.customerId);
      if (customerName) {
        return `${field.name} (${customerName})`;
      }
    }
    return field.name;
  }

  private initForm(): void {
    const today = new Date().toISOString().split('T')[0];

    this.form = this.fb.group({
      // Application Details
      date: [today, Validators.required],
      status: ['draft', Validators.required],

      // Field Information
      fieldId: ['', Validators.required],
      acreage: [{ value: '', disabled: true }],
      cropType: [{ value: '', disabled: true }],

      // Chemical Information - for adding chemicals
      chemicalSource: ['lookup'],
      chemicalTemplateId: [''],
      applicationRateValue: [null as number | null],
      applicationRateUnit: [DEFAULT_RATE_UNIT],
      totalVolume: [''],
      tankMixNotes: [''],

      // Certification
      certificationId: ['', Validators.required],

      // Weather Conditions (at start of mission - shared)
      temperature: [''],
      humidity: [''],
      windSpeed: ['', [Validators.min(0), Validators.max(50)]],
      windDirection: [''],
      weatherConditions: [''],

      // Spray Details
      whatWasTreated: [''],
      targetPest: [''],
      nozzleDescription: [''],
      nozzleToTargetDistance: [''],
      pumpPressure: [''],
      travelSpeed: [''],
      descriptionOfDriftManagementPractices: [''],

      // Visual Observer & NOTAM
      visualObserver: [''],
      voTested: [false],
      notam: [''],

      // Incident Information
      takeOffOrLandingDamage: [false],
      descriptionOfTakeOffOrLandingDamage: [''],
      equipmentMalfunction: [false],
      equipmentMalfunctionType: [''],
      descriptionOfEquipmentMalfunction: [''],
      hasAccident: [false],
      accidentType: [''],
      accidentDescription: [''],
      uaLossType: [''],

      // Application Types (ground record)
      appType_herbicide: [false],
      appType_pesticide: [false],
      appType_fungicide: [false],
      appType_fertilizer: [false],
      appType_other: [false],
      applicationTypeOther: [''],

      // Safety & Compliance (ground record)
      reentryInterval: [''],
      ppe_gloves: [false],
      ppe_coveralls: [false],
      ppe_apron: [false],
      ppe_faceShield: [false],
      ppe_goggles: [false],
      ppe_respirator: [false],
      ppe_rubberBoots: [false],
      rinseEquipmentPostApplication: [''],

      // Emergency Information (ground record)
      emergencyContactNumber: [''],
      stateEmergencyContactNumber: [''],

      // Load/Mix Location (ground record)
      useLocationForLoadMixLocation: [true],
      loadMixLocationLatitude: [''],
      loadMixLocationLongitude: [''],

      // Additional Info
      notes: [''],
      preFlightChecklist: [false],
      bufferZonesVerified: [false]
    });

    // Watch for field selection to auto-populate acreage, crop type, and customer
    this.form.get('fieldId')?.valueChanges.subscribe(fieldId => {
      this.selectedField = this.fields.find(f => f.id === fieldId);
      if (this.selectedField) {
        this.form.patchValue({
          acreage: this.selectedField.acreage,
          cropType: this.selectedField.cropType
        });
        // Update selected customer based on field's customerId
        if (this.selectedField.customerId) {
          const customer = this.customerService.getCustomerById(this.selectedField.customerId);
          this.selectedCustomer = customer ? { id: customer.id, name: customer.name } : undefined;
        } else {
          this.selectedCustomer = undefined;
        }
      } else {
        this.selectedCustomer = undefined;
      }
    });

    // Watch for certification selection
    this.form.get('certificationId')?.valueChanges.subscribe(certId => {
      this.selectedCertification = this.certifications.find(c => c.id === certId);
    });

    // Watch for template selection - auto-apply when selected
    this.form.get('chemicalTemplateId')?.valueChanges.subscribe(templateId => {
      this.selectedTemplate = this.chemicalTemplates.find(t => t.id === templateId);
      // Auto-apply template when selected (if templateId is set and template found)
      if (templateId && this.selectedTemplate) {
        this.applyTemplateChemicals(this.selectedTemplate);
      }
    });

    // Watch for chemical source changes to clear/reset selections
    this.form.get('chemicalSource')?.valueChanges.subscribe(source => {
      if (source === 'lookup') {
        this.form.patchValue({
          chemicalTemplateId: ''
        });
      } else {
        this.selectedChemical = null;
        this.form.patchValue({
          applicationRateValue: null,
          applicationRateUnit: DEFAULT_RATE_UNIT
        });
      }
    });
  }

  scrollToSection(id: string, event?: Event): void {
    event?.preventDefault(); // stop the "#" link from changing the URL
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Re-trigger the highlight even if it's clicked twice in a row
    el.classList.remove('section-highlight');
    void el.offsetWidth;
    el.classList.add('section-highlight');
    setTimeout(() => el.classList.remove('section-highlight'), 3000); // matches section-flash duration
  }

  toggleUnitSystem(flight: DroneFlightFormData): void {
    flight.unitSystem = flight.unitSystem === 'imperial' ? 'metric' : 'imperial';
  }



  // ============================================
  // FILE UPLOAD HANDLING
  // ============================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // dragleave also fires when moving onto a child element; ignore those
    const zone = event.currentTarget as HTMLElement;
    if (zone.contains(event.relatedTarget as Node)) return;
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      Array.from(files).forEach(file => this.processFile(file));
    }
  }

  private processFile(file: File): void {
    // Validate file type
    const validTypes = ['.kml', '.zip'];
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!validTypes.includes(extension)) {
      this.uploadError = 'Please upload a KML or ZIP file.';
      return;
    }

    this.isProcessing = true;
    this.uploadError = null;

    // Fill the first flight without a path, or add a new flight if they all have one
    let target = this.droneFlights.find(f => !f.flightPath);
    if (!target) {
      this.addDroneFlight();
      target = this.droneFlights[this.droneFlights.length - 1];
    }

    this.flightMapService.parseFlightFile(file).subscribe({
      next: (flightPaths) => {
        this.isProcessing = false;
        flightPaths.forEach(flightPath => {
          this.flightMapModalFlight = null;
          this.onFlightPathUploaded(flightPath);
        });
      },
      error: (err) => {
        this.isProcessing = false;
        this.uploadError = 'Failed to process file. Please try again.';
        console.error('File processing error:', err);
      }
    });
  }

  // ============================================
  // DRONE FLIGHT MANAGEMENT
  // ============================================

  addDroneFlight(): void {
    const newFlight: DroneFlightFormData = {
      id: this.generateId('flight'),
      droneId: '',
      droneSource: 'adn', // Default to ADN, will be updated when drone is selected
      pilotId: '',
      startTime: '',
      endTime: '',
      flightAltitude: null,
      flightSpeed: null,
      acreageSprayed: null,
      numFlights: null,
      flightPath: null,
      chemicalUsage: [],
      unitSystem: 'metric'
    };
    this.droneFlights.push(newFlight);
    this.updateAvailableDronesCache();
  }

  removeDroneFlight(index: number): void {
    if (this.droneFlights.length > 1) {
      const [removed] = this.droneFlights.splice(index, 1);
      this.flightConflicts.delete(removed.id);
      clearTimeout(this.conflictTimers.get(removed.id));
      this.conflictTimers.delete(removed.id);
      this.conflictTokens.delete(removed.id);
      this.updateAvailableDronesCache();
      this.updateTotalChemicalUsageCache();
    }
  }

  // Update available drones cache for all flights
  private updateAvailableDronesCache(): void {
    this.availableDronesMap.clear();
    this.droneFlights.forEach(flight => {
      const usedDroneIds = this.droneFlights
        .filter(f => f.id !== flight.id && f.droneId)
        .map(f => f.droneId);
      this.availableDronesMap.set(flight.id, this.drones.filter(d => !usedDroneIds.includes(d.id)));
    });
  }

  // Called when drone selection changes
  onDroneSelectionChange(flightIndex: number): void {
    // Set the droneSource based on the selected drone's ownership
    const flight = this.droneFlights[flightIndex];
    if (flight && flight.droneId) {
      const selectedDrone = this.drones.find(d => d.id === flight.droneId);
      if (selectedDrone) {
        flight.droneSource = selectedDrone.ownership === 'organization' ? 'organization' : 'adn';
      }
    }
    this.updateAvailableDronesCache();
    this.onFlightScheduleChange(flightIndex);
  }

  /**
   * Ask whether the aircraft is free the moment a row has a drone and both
   * times, so the clash surfaces at selection time rather than at save time.
   *
   * Timers and tokens are per row: editing row 2 must not cancel row 1's
   * check, and a slow answer must not overwrite a newer one.
   */
  onFlightScheduleChange(flightIndex: number): void {
    const flight = this.droneFlights[flightIndex];
    if (!flight) {
      return;
    }

    this.flightConflicts.delete(flight.id);
    clearTimeout(this.conflictTimers.get(flight.id));
    this.conflictTimers.delete(flight.id);

    // Bump before the early return, so an in-flight check is invalidated too.
    const token = (this.conflictTokens.get(flight.id) ?? 0) + 1;
    this.conflictTokens.set(flight.id, token);

    const applicationDate = this.form.get('date')?.value;
    if (!flight.droneId || !flight.startTime || !flight.endTime || !applicationDate) {
      return;
    }

    this.conflictTimers.set(flight.id, setTimeout(async () => {
      const result = await this.sprayRecordService.checkDroneAvailability({
        droneId: flight.droneId,
        droneSource: flight.droneSource,
        applicationDate,
        startTime: `${applicationDate}T${flight.startTime}:00`,
        endTime: `${applicationDate}T${flight.endTime}:00`,
        excludeSprayRecordId: this.recordId || undefined
      });

      if (this.conflictTokens.get(flight.id) !== token) {
        return;
      }

      if (result.available) {
        this.flightConflicts.delete(flight.id);
      } else {
        this.flightConflicts.set(flight.id, result.message || 'This drone is already booked for that time.');
      }
    }, 400));
  }

  /** Re-check every row — the date moved, so every window moved with it. */
  onApplicationDateChange(): void {
    this.droneFlights.forEach((_, index) => this.onFlightScheduleChange(index));
  }

  /**
   * A draft is still being worked on, so a clash must not stop the user
   * parking it. The row still shows the warning either way.
   */
  get hasBlockingFlightConflicts(): boolean {
    return this.flightConflicts.size > 0 && this.form.get('status')?.value !== 'draft';
  }

  // Get available drones for a flight (from cache)
  getAvailableDrones(flightId: string): Drone[] {
    return this.availableDronesMap.get(flightId) || this.drones;
  }

  // Check if we can add more drones
  get canAddDrone(): boolean {
    // Allow adding drones as long as there are drones available
    // Same drone can be used for multiple flights
    return this.drones.length > 0;
  }

  // Check if we should show the "Add Drone" button
  get showAddDroneButton(): boolean {
    // Show button if we have at least one drone available
    return this.drones.length > 0 && this.canAddDrone;
  }

  // Get drone name by ID (from cache)
  getDroneName(droneId: string): string {
    return this.droneNameMap.get(droneId) || '';
  }

  // Get pilot name by ID
  getPilotName(pilotId: string): string {
    return this.pilots.find(p => p.id === pilotId)?.name || '';
  }

  // Calculate chemical usage for a drone flight based on acreage
  calculateDroneChemicalUsage(flight: DroneFlightFormData): void {
    if (!flight.acreageSprayed) {
      flight.chemicalUsage = [];
      return;
    }

    flight.chemicalUsage = this.appliedChemicals.map(chem => {
      const { unit: rateUnit, perUnit: ratePerUnit } = parseRateUnit(chem.rateUnit);
      const amountUsed = calculateChemicalUsage(flight.acreageSprayed!, chem.rate);
      const parsedAmount = parseRate(amountUsed);
      return {
        appliedChemicalId: chem.id,
        chemicalName: chem.name,
        rate: chem.rate,
        rateValue: chem.rateValue,
        rateUnit,
        ratePerUnit,
        amountUsed,
        amountUsedValue: parsedAmount?.value ?? null,
        amountUsedUnit: parsedAmount?.unit ?? null
      };
    });
  }

  // Update acreage for a drone flight
  updateDroneAcreage(flight: DroneFlightFormData, value: string): void {
    const acreage = parseFloat(value);
    flight.acreageSprayed = isNaN(acreage) ? null : acreage;
    this.calculateDroneChemicalUsage(flight);
    this.updateTotalChemicalUsageCache();
  }

  // Handle flight path upload for a drone
  uploadFlightPath(flight: DroneFlightFormData): void {
    this.flightMapModalFlight = flight;
    this.showUploadOverlay = true;
    this.showFlightMapModal = true;
  }

  // Remove flight path from a drone
  removeFlightPath(flight: DroneFlightFormData): void {
    flight.flightPath = null;
  }

  // View flight path on map
  viewFlightPath(flight: DroneFlightFormData): void {
    this.flightMapModalFlight = flight;
    this.showUploadOverlay = false;
    this.showFlightMapModal = true;
  }

  // Handle file uploaded from flight map component
  onFlightPathUploaded(flightPath: FlightPathData): void {
    // Later flights from the same zip arrive after the modal closed: give each its own drone flight
    if (!this.flightMapModalFlight) {
      this.flightMapModalFlight = this.droneFlights.find(f => !f.flightPath) ?? null;
      if (!this.flightMapModalFlight) {
        this.addDroneFlight();
        this.flightMapModalFlight = this.droneFlights[this.droneFlights.length - 1];
      }
    }

    if (this.flightMapModalFlight) {
      this.flightMapModalFlight.flightPath = flightPath;

      this.flightMapModalFlight.numFlights = 1;

      // Auto-populate acreage from flight path if calculated
      if (flightPath.areaAcres) {
        this.flightMapModalFlight.acreageSprayed = flightPath.areaAcres;
        this.calculateDroneChemicalUsage(this.flightMapModalFlight);
        this.updateTotalChemicalUsageCache();
      }

      // Auto-populate flight altitude
      if(flightPath.altitude){
        this.flightMapModalFlight.flightAltitude = flightPath.altitude;
      }

      // Auto-populate flight speed
      if(flightPath.speed){
        this.flightMapModalFlight.flightSpeed = flightPath.speed;
      }

      // Auto-populate drone information)
      if(flightPath.droneId || flightPath.droneName){
        const matchedDrone =
          (flightPath.droneId && this.findDroneByKml(this.flightMapModalFlight, flightPath.droneId)) ||
          (flightPath.droneName && this.findDroneByKml(this.flightMapModalFlight, flightPath.droneName)) || undefined;
        if (matchedDrone) {
          this.flightMapModalFlight.droneId = matchedDrone.id;
          this.onDroneSelectionChange(this.droneFlights.indexOf(this.flightMapModalFlight));
        }
      }

      // Auto-populate pilot information 
      if(flightPath.pilotName){
        const matchedPilot = this.findPilotByKmlName(flightPath.pilotName);
        if (matchedPilot) {
          this.flightMapModalFlight.pilotId = matchedPilot.id;
        }
      }

      // Auto-populate flight start and end times
      if(flightPath.startTime) {
        // set the start time
        const [date, time] = flightPath.startTime.split('T');
        this.form.patchValue({ date });
        if (time) {
          this.flightMapModalFlight.startTime = time.slice(0, 5);
          
          //try setting the weather
          this.lookupWeather()
        }
      }
      if(flightPath.endTime) {
        const time = flightPath.endTime.split('T')[1];
        if (time) {
          this.flightMapModalFlight.endTime = time.slice(0, 5);
        }
      }
    }

    // Highlight required drone flight fields the KML couldn't fill
    this.showDroneFlightErrors = true;

    this.closeFlightMapModal();
  }

  // Match a KML aircraft name to a drone this flight can use (name or serial, case-insensitive)
  private findDroneByKml(flight: DroneFlightFormData, kmlNameOrId: string): Drone | undefined {
    const target = kmlNameOrId.trim().toLowerCase();
    return this.getAvailableDrones(flight.id).find(d =>
      d.name?.trim().toLowerCase() === target || d.serialNumber?.trim().toLowerCase() === target
    );
  }

  // Match a KML pilot name to a pilot: exact full name first, then a unique first-name match
  private findPilotByKmlName(kmlName: string): Pilot | undefined {
    const target = kmlName.trim().toLowerCase();
    const exact = this.pilots.find(p => p.name?.trim().toLowerCase() === target);
    if (exact) return exact;

    const firstNameMatches = this.pilots.filter(p => p.name?.trim().toLowerCase().split(/\s+/)[0] === target);
    return firstNameMatches.length === 1 ? firstNameMatches[0] : undefined;
  }

  // Close the flight map modal
  closeFlightMapModal(): void {
    this.showFlightMapModal = false;
    this.showUploadOverlay = false;
    this.flightMapModalFlight = null;
  }

  // Get field center for map positioning
  getFieldCenter(): { lat: number; lng: number } | null {
    if (this.selectedField?.latitude && this.selectedField?.longitude) {
      return { lat: this.selectedField.latitude, lng: this.selectedField.longitude };
    }
    return null;
  }

  // Get all flight paths for multi-drone map view
  getAllFlightPaths(): FlightPathData[] {
    return this.droneFlights
      .filter(f => f.flightPath !== null)
      .map(f => f.flightPath!);
  }

  // View all flight paths on map
  viewAllFlightPaths(): void {
    this.flightMapModalFlight = null;
    this.showUploadOverlay = false;
    this.showFlightMapModal = true;
  }

  // ============================================
  // TOTALS CALCULATIONS
  // ============================================

  get totalAcreageSprayed(): number {
    return this.droneFlights.reduce((sum, f) => sum + (f.acreageSprayed || 0), 0);
  }

  get fieldAcreage(): number {
    return this.selectedField?.acreage || 0;
  }

  get acreageRemaining(): number {
    return Math.max(0, this.fieldAcreage - this.totalAcreageSprayed);
  }

  // Update cached total chemical usage
  private updateTotalChemicalUsageCache(): void {
    // Liquids in fl oz; for lb chemicals totalOz holds pounds
    const totals = new Map<string, { name: string; totalOz: number; isLb: boolean }>();

    this.droneFlights.forEach(flight => {
      flight.chemicalUsage.forEach(cu => {
        const parsed = parseRate(cu.amountUsed);
        if (parsed) {
          // lb is weight: add pounds as-is; liquids are converted to fl oz
          const oz = parsed.unit === 'lb' ? parsed.value : toOz(parsed.value, parsed.unit);
          if (oz == null) return; // skip unknown units
          const existing = totals.get(cu.appliedChemicalId);
          if (existing) {
            existing.totalOz += oz;
          } else {
            totals.set(cu.appliedChemicalId, {
              name: cu.chemicalName,
              totalOz: oz,
              isLb: parsed.unit === 'lb'
            });
          }
        }
      });
    });

    const result: { chemicalName: string; totalAmount: string }[] = [];
    totals.forEach((value) => {
      const best = formatBestUnit(value.totalOz);
      result.push({
        chemicalName: value.name,
        totalAmount: value.isLb
          ? `${value.totalOz.toFixed(2)} lb`
          : `${best.value.toFixed(2)} ${best.unit}`
      });
    });

    this.totalChemicalUsage = result;
  }

  // ============================================
  // TRACKBY FUNCTIONS (for ngFor performance)
  // ============================================

  trackByFlightId(index: number, flight: DroneFlightFormData): string {
    return flight.id;
  }

  trackByChemicalId(index: number, chemical: AppliedChemical): string {
    return chemical.id;
  }

  trackByDroneId(index: number, drone: Drone): string {
    return drone.id;
  }

  trackByPilotId(index: number, pilot: Pilot): string {
    return pilot.id;
  }

  trackByChemicalUsageId(index: number, cu: ChemicalUsage): string {
    return cu.appliedChemicalId;
  }

  trackByUsageIndex(index: number, usage: { chemicalName: string; totalAmount: string }): number {
    return index;
  }

  // ============================================
  // CHEMICAL MANAGEMENT (unchanged mostly)
  // ============================================

  addChemical(): void {
    const rateValue = this.form.get('applicationRateValue')?.value;
    const rateUnit = this.form.get('applicationRateUnit')?.value || DEFAULT_RATE_UNIT;

    if (!this.selectedChemical || !rateValue) {
      return;
    }

    if (this.appliedChemicals.some(ac => ac.chemicalId === this.selectedChemical?.id)) {
      return;
    }

    const sourceMap: Record<string, 'book' | 'custom'> = {
      'epa': 'book',
      'adn-recommended': 'book',
      'custom': 'custom'
    };

    const rate = buildRateString(rateValue, rateUnit);

    const appliedChemical: AppliedChemical = {
      id: this.generateId('chem'),
      chemicalId: this.selectedChemical.id,
      name: this.selectedChemical.name,
      epaNumber: this.selectedChemical.epaNumber,
      rate,
      rateValue,
      rateUnit,
      source: sourceMap[this.selectedChemical.source] || 'book'
    };

    this.appliedChemicals.push(appliedChemical);

    // Recalculate all drone flight chemical usage
    this.recalculateAllDroneChemicalUsage();

    this.selectedChemical = null;
    this.form.patchValue({
      applicationRateValue: null,
      applicationRateUnit: DEFAULT_RATE_UNIT
    });

    // Also clear the search box
    this.chemicalTypeahead?.clearSelection();
  }

  onChemicalSelected(chemical: Chemical): void {
    this.selectedChemical = chemical;
    // Parse the default rate string into structured fields
    const parsed = chemical.defaultRate ? parseRateToStructured(chemical.defaultRate) : null;
    this.form.patchValue({
      applicationRateValue: parsed?.value ?? null,
      applicationRateUnit: parsed?.rateUnit ?? DEFAULT_RATE_UNIT
    });
  }

  onChemicalCleared(): void {
    this.selectedChemical = null;
    this.form.patchValue({
      applicationRateValue: null,
      applicationRateUnit: DEFAULT_RATE_UNIT
    });
  }

  applyTemplate(): void {
    const templateId = this.form.get('chemicalTemplateId')?.value;

    if (!templateId) {
      return;
    }

    const template = this.chemicalTemplates.find(t => t.id === templateId);
    if (!template) {
      return;
    }

    this.applyTemplateChemicals(template);
  }

  /**
   * Apply chemicals from a template (used by both manual apply and auto-apply on selection)
   */
  private applyTemplateChemicals(template: ChemicalTemplateModel): void {
    if (!template.items) {
      return;
    }

    template.items.forEach(item => {
      if (this.appliedChemicals.some(ac => ac.name === item.name)) {
        return;
      }

      const parsed = item.rate ? parseRateToStructured(item.rate) : null;
      const rateUnit = parsed?.rateUnit ?? DEFAULT_RATE_UNIT;
      const rateValue = parsed?.value ?? null;

      const appliedChemical: AppliedChemical = {
        id: this.generateId('chem'),
        chemicalId: item.chemicalId || item.customChemicalId || undefined,
        name: item.name,
        epaNumber: item.epaNumber || 'N/A',
        rate: item.rate,
        rateValue,
        rateUnit,
        source: 'template',
        templateName: template.name
      };

      this.appliedChemicals.push(appliedChemical);
    });

    // Recalculate all drone flight chemical usage
    this.recalculateAllDroneChemicalUsage();

    // Clear the template selection after applying
    this.form.patchValue({
      chemicalTemplateId: ''
    });
  }

  removeChemical(appliedChemical: AppliedChemical): void {
    const index = this.appliedChemicals.findIndex(ac => ac.id === appliedChemical.id);
    if (index > -1) {
      this.appliedChemicals.splice(index, 1);
      // Recalculate all drone flight chemical usage
      this.recalculateAllDroneChemicalUsage();
    }
  }

  updateChemicalRateValue(appliedChemical: AppliedChemical, newValue: number | null): void {
    const chemical = this.appliedChemicals.find(ac => ac.id === appliedChemical.id);
    if (chemical) {
      chemical.rateValue = newValue;
      chemical.rate = newValue != null ? buildRateString(newValue, chemical.rateUnit) : '';
      this.recalculateAllDroneChemicalUsage();
    }
  }

  updateChemicalRateUnit(appliedChemical: AppliedChemical, newUnit: string): void {
    const chemical = this.appliedChemicals.find(ac => ac.id === appliedChemical.id);
    if (chemical) {
      chemical.rateUnit = newUnit;
      chemical.rate = chemical.rateValue != null ? buildRateString(chemical.rateValue, newUnit) : '';
      this.recalculateAllDroneChemicalUsage();
    }
  }

  private recalculateAllDroneChemicalUsage(): void {
    this.droneFlights.forEach(flight => {
      this.calculateDroneChemicalUsage(flight);
    });
    this.updateTotalChemicalUsageCache();
  }

  /** Coerce a form value that may be '' / null / a numeric string into a number or null. */
  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getSourceBadgeClass(source: string): string {
    switch (source) {
      case 'book':
        return 'bg-primary-subtle text-primary';
      case 'custom':
        return 'bg-info-subtle text-info';
      case 'template':
        return 'bg-success-subtle text-success';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'book':
        return 'Book';
      case 'custom':
        return 'Custom';
      case 'template':
        return 'Template';
      default:
        return source;
    }
  }

  get isChemicalLookup(): boolean {
    return this.form.get('chemicalSource')?.value === 'lookup';
  }

  get isChemicalTemplate(): boolean {
    return this.form.get('chemicalSource')?.value === 'template';
  }

  // ============================================
  // INCIDENT VISIBILITY GETTERS
  // ============================================

  get showTakeOffDamageDescription(): boolean {
    return this.form.get('takeOffOrLandingDamage')?.value || false;
  }

  get showEquipmentMalfunctionDetails(): boolean {
    return this.form.get('equipmentMalfunction')?.value || false;
  }

  get showAccidentDetails(): boolean {
    return this.form.get('hasAccident')?.value || false;
  }

  // Ground record visibility getters
  get showApplicationTypeOther(): boolean {
    return this.form.get('appType_other')?.value || false;
  }

  get showLoadMixLocationFields(): boolean {
    return !this.form.get('useLocationForLoadMixLocation')?.value;
  }

  // Note: selectedTemplate is cached and updated via form subscription

  // ============================================
  // FORM LIFECYCLE
  // ============================================

  private checkEditMode(): void {
    this.recordId = this.route.snapshot.paramMap.get('id');
    if (this.recordId) {
      this.isEditMode = true;
      this.loadRecord(this.recordId);
    }
  }

  private async loadRecord(id: string): Promise<void> {
    this.isLoading = true;
    console.log('Loading record:', id);

    try {
      // Fetch record from API
      const record = await this.sprayRecordService.getRecordById(id);

      console.log('=== LOADED RECORD FROM API ===');
      console.log('record.certificationId:', record?.certificationId);
      console.log('Full record:', record);
      console.log('==============================');

      if (!record) {
        console.error('Record not found');
        this.isLoading = false;
        return;
      }

      // Patch form with basic fields
      this.form.patchValue({
        date: toDateInputValue(record.applicationDate),
        status: record.status,
        fieldId: record.fieldId,
        // Spray details (from nested object or flat fields for backwards compatibility)
        whatWasTreated: record.sprayDetails?.whatWasTreated || record.whatWasTreated || '',
        targetPest: record.sprayDetails?.targetPest || record.targetPest || '',
        nozzleDescription: record.sprayDetails?.nozzleDescription || record.nozzleDescription || '',
        nozzleToTargetDistance: record.sprayDetails?.nozzleToTargetDistance || record.nozzleToTargetDistance || '',
        pumpPressure: record.sprayDetails?.pumpPressure || record.pumpPressure || '',
        travelSpeed: record.sprayDetails?.travelSpeed || record.travelSpeed || '',
        descriptionOfDriftManagementPractices: record.sprayDetails?.descriptionOfDriftManagementPractices || record.driftManagementPractices || '',
        applicationTypeOther: record.applicationTypeInfo?.applicationTypeOther || record.applicationTypeOther || '',
        totalVolume: record.totalVolume || '',
        tankMixNotes: record.tankMixNotes || '',
        certificationId: record.certificationId,
        // Weather
        temperature: record.weather?.temperature || null,
        humidity: record.weather?.humidity || null,
        windSpeed: record.weather?.windSpeed || null,
        windDirection: record.weather?.windDirection || '',
        weatherConditions: record.weather?.conditions || '',
        // Visual Observer
        visualObserver: record.visualObserverInfo?.visualObserver || '',
        voTested: record.visualObserverInfo?.voTested || false,
        notam: record.visualObserverInfo?.notam || '',
        // Incident information
        // NOTE: the Hub nests these as description*OfTakeOffOrLandingDamage /
        // descriptionOfEquipmentMalfunction (see getWithRelated). Reading the
        // flat column names instead silently blanked the descriptions on edit,
        // and re-saving then wiped them from the record.
        takeOffOrLandingDamage: record.incidentInfo?.takeOffOrLandingDamage || false,
        descriptionOfTakeOffOrLandingDamage:
          record.incidentInfo?.descriptionOfTakeOffOrLandingDamage
          ?? record.incidentInfo?.takeOffOrLandingDamageDescription
          ?? '',
        equipmentMalfunction: record.incidentInfo?.equipmentMalfunction || false,
        equipmentMalfunctionType: record.incidentInfo?.equipmentMalfunctionType || '',
        descriptionOfEquipmentMalfunction:
          record.incidentInfo?.descriptionOfEquipmentMalfunction
          ?? record.incidentInfo?.equipmentMalfunctionDescription
          ?? '',
        hasAccident: record.incidentInfo?.hasAccident || false,
        accidentType: record.incidentInfo?.accidentType || '',
        accidentDescription: record.incidentInfo?.accidentDescription || '',
        uaLossType: record.incidentInfo?.uaLossType || '',
        // Safety & compliance
        reentryInterval: record.safetyCompliance?.reentryInterval || '',
        rinseEquipmentPostApplication: record.safetyCompliance?.rinseEquipmentPostApplication || '',
        // Emergency contacts
        emergencyContactNumber: record.emergencyContact?.emergencyContactNumber || '',
        stateEmergencyContactNumber: record.emergencyContact?.stateEmergencyContactNumber || '',
        // Load/mix location
        useLocationForLoadMixLocation: record.loadMixLocation?.useLocationForLoadMixLocation ?? true,
        loadMixLocationLatitude: record.loadMixLocation?.loadMixLocationLatitude ?? '',
        loadMixLocationLongitude: record.loadMixLocation?.loadMixLocationLongitude ?? '',
        // Checklist
        notes: record.notes || '',
        preFlightChecklist: record.preFlightChecklist || false,
        bufferZonesVerified: record.bufferZonesVerified || false
      });

      // Set application type checkboxes (from nested object or flat field)
      const appTypes = record.applicationTypeInfo?.applicationTypes || record.applicationTypes || [];
      if (Array.isArray(appTypes)) {
        this.form.patchValue({
          appType_herbicide: appTypes.includes('herbicide'),
          appType_pesticide: appTypes.includes('pesticide'),
          appType_fungicide: appTypes.includes('fungicide'),
          appType_fertilizer: appTypes.includes('fertilizer'),
          appType_other: appTypes.includes('other')
        });
      }

      // Set PPE checkboxes
      const ppeList = record.safetyCompliance?.ppeRequired || [];
      if (Array.isArray(ppeList)) {
        this.form.patchValue({
          ppe_gloves: ppeList.includes('gloves'),
          ppe_coveralls: ppeList.includes('coveralls'),
          ppe_apron: ppeList.includes('apron'),
          ppe_faceShield: ppeList.includes('faceShield'),
          ppe_goggles: ppeList.includes('goggles'),
          ppe_respirator: ppeList.includes('respirator'),
          ppe_rubberBoots: ppeList.includes('rubberBoots')
        });
      }

      // Set selected certification (wait for certifications to load if needed)
      if (record.certificationId) {
        this.setSelectedCertification(record.certificationId);
      }

      // Load applied chemicals
      if (record.chemicals && record.chemicals.length > 0) {
        this.appliedChemicals = record.chemicals.map((chem: any) => {
          // Use structured fields from API if available, otherwise parse from rate string
          const parsed = chem.rate ? parseRateToStructured(chem.rate) : null;
          const rateValue = chem.rateValue ?? parsed?.value ?? null;
          const rateUnit = (chem.rateUnit && chem.ratePerUnit)
            ? `${chem.rateUnit}/${chem.ratePerUnit}`
            : parsed?.rateUnit ?? DEFAULT_RATE_UNIT;

          return {
            id: chem.id,
            chemicalId: chem.chemicalId,
            name: chem.name,
            epaNumber: chem.epaNumber || '',
            rate: chem.rate || '',
            rateValue,
            rateUnit,
            source: chem.source || 'book',
            templateName: chem.templateName
          };
        });
      }

      // Load drone flights
      if (record.droneFlights && record.droneFlights.length > 0) {
        this.droneFlights = record.droneFlights.map((flight: any) => ({
          id: flight.id,
          droneId: flight.droneId,
          droneSource: flight.droneSource || 'adn',
          droneName: flight.droneName || null,
          pilotId: flight.pilotId,
          pilotName: flight.pilotName || null,
          // Extract time-only part from ISO timestamp (e.g., "2026-01-09T14:10:00" -> "14:10")
          startTime: this.extractTimeFromTimestamp(flight.startTime),
          endTime: this.extractTimeFromTimestamp(flight.endTime),
          flightAltitude: flight.flightAltitude || null,
          flightSpeed: flight.flightSpeed || null,
          acreageSprayed: flight.acreageSprayed || null,
          // `?? null`, not `|| null` — a stored 0 is a real fact ("this
          // aircraft flew zero times") and must not collapse to "not entered".
          numFlights: normalizeNumFlights(flight.numFlights),
          flightPath: flight.flightPath || null,
          chemicalUsage: flight.chemicalUsage || [],
          unitSystem: flight.unitSystem || 'metric'
        }));

        // A stored record can already clash — another job may have been booked
        // on this aircraft since. Nothing on screen has changed, so only an
        // explicit check surfaces it.
        this.droneFlights.forEach((_, index) => this.onFlightScheduleChange(index));
      }

      // Calculate chemical usage for loaded flights
      this.recalculateAllDroneChemicalUsage();

      this.isLoading = false;
    } catch (error) {
      console.error('Error loading record:', error);
      this.isLoading = false;
    }
  }

  /**
   * Set selected certification, waiting for certifications to load if needed
   */
  private setSelectedCertification(certificationId: string): void {
    console.log('=== setSelectedCertification ===');
    console.log('Looking for certificationId:', certificationId);
    console.log('Available certifications:', this.certifications);
    console.log('isLoadingCertifications:', this.isLoadingCertifications);

    // Try to find immediately if certifications are loaded
    const found = this.certifications.find(c => c.id === certificationId);
    console.log('Found immediately:', found);
    if (found) {
      this.selectedCertification = found;
      console.log('Set selectedCertification immediately');
      return;
    }

    // If certifications aren't loaded yet, poll until they are (max 5 seconds)
    console.log('Certifications not loaded yet, starting poll...');
    const maxAttempts = 50;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      const cert = this.certifications.find(c => c.id === certificationId);
      if (cert) {
        this.selectedCertification = cert;
        console.log('Found certification after', attempts, 'attempts:', cert);
        clearInterval(interval);
      } else if (attempts >= maxAttempts || !this.isLoadingCertifications) {
        // Stop trying after max attempts or when loading is done
        console.log('Stopped polling after', attempts, 'attempts. isLoadingCertifications:', this.isLoadingCertifications);
        console.log('Final certifications array:', this.certifications);
        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * Extract time-only part from ISO timestamp or datetime string
   * e.g., "2026-01-09T14:10:00" -> "14:10"
   * e.g., "2026-01-09T14:10:00.000Z" -> "14:10"
   */
  private extractTimeFromTimestamp(timestamp: string | null | undefined): string {
    if (!timestamp) return '';

    // If it's already in time-only format (HH:MM), return as-is
    if (/^\d{2}:\d{2}$/.test(timestamp)) {
      return timestamp;
    }

    // Extract time directly from ISO string to avoid timezone conversion
    // Format: "2026-01-09T14:10:00" or "2026-01-09T14:10:00.000Z"
    try {
      const match = timestamp.match(/T(\d{2}):(\d{2})/);
      if (match) {
        return `${match[1]}:${match[2]}`;
      }

      // Fallback: try parsing as Date (may have timezone issues)
      const date = new Date(timestamp);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('Failed to parse timestamp:', timestamp, error);
      return '';
    }
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // Note: selectedField is cached and updated via form subscription

  // ============================================
  // COORDINATE DISPLAY
  // ============================================

  coordinateDisplayMode: 'decimal' | 'dms' = 'decimal';

  toggleCoordinateDisplay(): void {
    this.coordinateDisplayMode = this.coordinateDisplayMode === 'decimal' ? 'dms' : 'decimal';
  }

  decimalToDMS(decimal: number, isLatitude: boolean): string {
    const absolute = Math.abs(decimal);
    const degrees = Math.floor(absolute);
    const minutesFloat = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesFloat);
    const seconds = ((minutesFloat - minutes) * 60).toFixed(2);

    let direction: string;
    if (isLatitude) {
      direction = decimal >= 0 ? 'N' : 'S';
    } else {
      direction = decimal >= 0 ? 'E' : 'W';
    }

    return `${degrees}°${minutes}'${seconds}"${direction}`;
  }

  formatCoordinates(field: Field | undefined): string {
    if (!field || field.latitude === null || field.longitude === null) {
      return 'No coordinates';
    }

    if (this.coordinateDisplayMode === 'decimal') {
      return `${field.latitude.toFixed(6)}, ${field.longitude.toFixed(6)}`;
    } else {
      return `${this.decimalToDMS(field.latitude, true)} ${this.decimalToDMS(field.longitude, false)}`;
    }
  }

  copyCoordinates(): void {
    if (this.selectedField && this.selectedField.latitude !== null && this.selectedField.longitude !== null) {
      const text = `${this.selectedField.latitude}, ${this.selectedField.longitude}`;
      navigator.clipboard.writeText(text);
    }
  }

  openInMaps(): void {
    if (this.selectedField && this.selectedField.latitude !== null && this.selectedField.longitude !== null) {
      const url = `https://www.google.com/maps?q=${this.selectedField.latitude},${this.selectedField.longitude}`;
      window.open(url, '_blank');
    }
  }

  // ============================================
  // WEATHER LOOKUP
  // ============================================

  // Weather location: selected field's coordinates, else the first point of an uploaded flight path
  get weatherLocation(): { lat: number; lng: number } | null {
    if (this.selectedField?.latitude && this.selectedField?.longitude) {
      return { lat: this.selectedField.latitude, lng: this.selectedField.longitude };
    }
    return this.droneFlights.find(f => f.flightPath?.coordinates?.length)?.flightPath?.coordinates[0] ?? null;
  }

  get canLookupWeather(): boolean {
    const date = this.form.get('date')?.value;
    // Use start time from first drone flight
    const startTime = this.droneFlights.length > 0 ? this.droneFlights[0].startTime : null;
    return !!(this.weatherLocation?.lat && this.weatherLocation?.lng && date && startTime);
  }

  lookupWeather(): void {
    const date = this.form.get('date')?.value;
    const startTime = this.droneFlights.length > 0 ? this.droneFlights[0].startTime : null;
    const location = this.weatherLocation;

    if (!location || !date || !startTime) {
      this.weatherError = 'Please select a field with coordinates, date, and start time first.';
      return;
    }

    this.isLoadingWeather = true;
    this.weatherError = null;
    this.weatherLookupSuccess = false;

    this.weatherService.getWeather(location.lat, location.lng, date, startTime)
      .subscribe({
        next: (weather: WeatherData) => {
          // Only overwrite Conditions when the API gave us a mappable code
          // (empty string means unknown WMO code) — preserves anything the
          // user already typed in that case.
          const conditionsPatch = weather.conditions
            ? { weatherConditions: weather.conditions }
            : {};
          this.form.patchValue({
            temperature: weather.temperature,
            humidity: weather.humidity,
            windSpeed: weather.windSpeed,
            windDirection: weather.windDirection,
            ...conditionsPatch,
          });
          this.isLoadingWeather = false;
          this.weatherLookupSuccess = true;

          setTimeout(() => {
            this.weatherLookupSuccess = false;
          }, 3000);
        },
        error: (err: Error) => {
          console.error('Weather lookup failed:', err);
          this.weatherError = err.message || 'Unable to fetch weather data. Please try again.';
          this.isLoadingWeather = false;
        }
      });
  }

  // ============================================
  // FIELD QUICK ADD
  // ============================================

  openFieldQuickAdd(): void {
    this.fieldQuickAdd?.open();
  }

  onFieldCreated(newField: FieldModel): void {
    // Refresh fields cache to include the new field
    this.fields = this.fieldService.getFieldOptions();

    // Directly set selectedField from the newly created field to ensure coordinates are available
    // This avoids any timing issues with signal propagation
    this.selectedField = {
      id: newField.id,
      organizationId: newField.organizationId || '',
      customerId: newField.customerId,
      name: newField.name,
      acreage: newField.acreage,
      cropType: newField.cropType,
      latitude: newField.latitude ?? null,
      longitude: newField.longitude ?? null,
      createdAt: newField.createdAt || '',
      updatedAt: newField.updatedAt || ''
    } as FieldModel;

    // Update selected customer if field has one
    if (newField.customerId) {
      const customer = this.customerService.getCustomerById(newField.customerId);
      this.selectedCustomer = customer ? { id: customer.id, name: customer.name } : undefined;
    }

    this.form.patchValue({
      fieldId: newField.id,
      acreage: newField.acreage,
      cropType: newField.cropType
    });
  }

  // ============================================
  // FORM SUBMISSION
  // ============================================

  private getSelectedApplicationTypes(): string[] {
    const formValue = this.form.getRawValue();
    const types: string[] = [];

    if (formValue.appType_herbicide) types.push('herbicide');
    if (formValue.appType_pesticide) types.push('pesticide');
    if (formValue.appType_fungicide) types.push('fungicide');
    if (formValue.appType_fertilizer) types.push('fertilizer');
    if (formValue.appType_other) types.push('other');

    return types;
  }

  private getSelectedPPE(): string[] {
    const formValue = this.form.getRawValue();
    const ppe: string[] = [];

    if (formValue.ppe_gloves) ppe.push('gloves');
    if (formValue.ppe_coveralls) ppe.push('coveralls');
    if (formValue.ppe_apron) ppe.push('apron');
    if (formValue.ppe_faceShield) ppe.push('faceShield');
    if (formValue.ppe_goggles) ppe.push('goggles');
    if (formValue.ppe_respirator) ppe.push('respirator');
    if (formValue.ppe_rubberBoots) ppe.push('rubberBoots');

    return ppe;
  }

  async onSubmit(): Promise<void> {
    const isDraft = this.form.get('status')?.value === 'draft';
    this.validationErrors = [];
    this.genericError = '';
    this.showDroneFlightErrors = false;

    if (isDraft) {
      // Drafts only require a date
      if (!this.form.get('date')?.value) {
        this.form.get('date')?.markAsTouched();
        this.validationErrors.push('Date is required');
        return;
      }
    } else {
      // Mark all form fields as touched to trigger visual feedback
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach(key => {
          this.form.get(key)?.markAsTouched();
        });
      }

      // Collect all validation errors
      if (!this.form.get('date')?.value) {
        this.validationErrors.push('Date is required');
      }
      if (!this.form.get('fieldId')?.value) {
        this.validationErrors.push('Field is required');
      }
      if (!this.form.get('certificationId')?.value) {
        this.validationErrors.push('FAA 44807 Exemption is required');
      }

      // Validate drone flights (flight path is optional)
      // End time is required: without it there is no flight window, so the
      // aircraft cannot be checked against anyone else's booking.
      const hasValidFlights = this.droneFlights.every(f =>
        f.droneId && f.pilotId && f.startTime && f.endTime && f.acreageSprayed
      );

      if (this.droneFlights.length > 0 && !hasValidFlights) {
        this.showDroneFlightErrors = true;
        const missingFields: string[] = [];
        if (this.droneFlights.some(f => !f.droneId)) missingFields.push('drone');
        if (this.droneFlights.some(f => !f.pilotId)) missingFields.push('pilot');
        if (this.droneFlights.some(f => !f.startTime)) missingFields.push('start time');
        if (this.droneFlights.some(f => !f.endTime)) missingFields.push('end time');
        if (this.droneFlights.some(f => !f.acreageSprayed)) missingFields.push('acreage sprayed');
        this.validationErrors.push(`Drone flights are missing required fields: ${missingFields.join(', ')}`);
      }

      if (this.flightConflicts.size > 0) {
        this.showDroneFlightErrors = true;
        this.flightConflicts.forEach(message => this.validationErrors.push(message));
      }

      // FAA §F.2.d — validate the per-aircraft flight count.
      //
      // `min="1"` on the input is decorative: with `[(ngModel)]` and
      // `standalone: true` and no `Validators.min`, neither the browser nor
      // Angular rejects a typed 0, a decimal or a negative. Blank is the only
      // legitimate "not determined" and must stay allowed.
      //
      // Why this is enforced rather than normalised away: the spec chose
      // `min=1` precisely so the screen and the filing can never disagree. A
      // stored 0 reads back as "0" but files as 1, which is the one
      // contradiction that decision existed to prevent. A fractional value is
      // worse — the column is `integer`, so Postgres would round it and file a
      // number nobody entered. Rejecting is the only option that neither lies
      // nor silently discards what the user typed.
      const invalidNumFlights = this.droneFlights.some(f => {
        // Angular's number input yields null when cleared, so null/undefined
        // is the blank case — "not determined", and the intended way to skip.
        const value = f.numFlights;
        if (value === null || value === undefined) {
          return false;
        }
        return !Number.isInteger(Number(value)) || Number(value) < 1;
      });

      if (invalidNumFlights) {
        this.showDroneFlightErrors = true;
        this.validationErrors.push(
          'Number of Flights must be a whole number of 1 or more, or left blank if unknown. ' +
          'An aircraft that did not fly should be removed from the record.'
        );
      }

      // Validate at least one chemical
      if (this.appliedChemicals.length === 0) {
        this.validationErrors.push('At least one chemical is required');
      }

      if (this.validationErrors.length > 0) {
        return;
      }
    }

    console.log('=== VALIDATION RESULTS ===');
    console.log('Drone flights count:', this.droneFlights.length);
    console.log('Drone flights array:', this.droneFlights);
    console.log('Applied chemicals count:', this.appliedChemicals.length);
    console.log('==========================');

    this.isSaving = true;

    try {
      const formValue = this.form.getRawValue();

      // Map applied chemicals to API format
      const chemicals = this.appliedChemicals.map(chem => {
        const { unit, perUnit } = parseRateUnit(chem.rateUnit);
        return {
          chemicalId: chem.chemicalId || undefined,
          name: chem.name,
          epaNumber: chem.epaNumber,
          rate: chem.rate,
          rateValue: chem.rateValue,
          rateUnit: unit,
          ratePerUnit: perUnit,
          source: chem.source,
          templateName: chem.templateName
        };
      });

      // Helper to check if value is a valid UUID
      const isValidUuid = (value: string): boolean => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(value);
      };

      // Debug certification
      console.log('=== CERTIFICATION DEBUG ===');
      console.log('formValue.certificationId:', formValue.certificationId);
      console.log('isValidUuid(certificationId):', formValue.certificationId ? isValidUuid(formValue.certificationId) : 'N/A');
      console.log('selectedCertification:', this.selectedCertification);
      console.log('===========================');

      // Map drone flights to API format - filter out flights with invalid UUIDs
      console.log('=== BEFORE FILTERING DRONE FLIGHTS ===');
      this.droneFlights.forEach((flight, index) => {
        console.log(`Flight ${index}:`, {
          droneId: flight.droneId,
          isDroneIdValid: isValidUuid(flight.droneId),
          pilotId: flight.pilotId,
          isPilotIdValid: isValidUuid(flight.pilotId),
          startTime: flight.startTime,
          acreageSprayed: flight.acreageSprayed
        });
      });
      console.log('======================================');

      const droneFlights = this.droneFlights
        .filter(flight => {
          // droneId must be a valid UUID (references adn_drones or adn_organization_drones)
          // pilotId can be Firebase UID or UUID, just needs to be non-empty
          const isValid = isValidUuid(flight.droneId) && flight.pilotId && flight.pilotId.length > 0;
          if (!isValid) {
            console.warn('Filtering out invalid flight:', {
              droneId: flight.droneId,
              isDroneIdValid: isValidUuid(flight.droneId),
              pilotId: flight.pilotId,
              isPilotIdPresent: !!(flight.pilotId && flight.pilotId.length > 0),
              reason: !isValidUuid(flight.droneId) ? 'Invalid droneId UUID' : 'Missing pilotId'
            });
          }
          return isValid;
        })
        .map(flight => buildDroneFlightPayload(
          flight,
          this.drones.find(d => d.id === flight.droneId),
          this.pilots.find(p => p.id === flight.pilotId),
          formValue.date // e.g. "2026-01-09"
        ));

      const dto: CreateSprayRecordDto = {
        fieldId: formValue.fieldId,
        applicationDate: formValue.date, // Form field is named 'date'
        status: formValue.status || 'completed',
        // certificationId now refers to the 44807 (operating certification)
        certificationId: formValue.certificationId && isValidUuid(formValue.certificationId)
          ? formValue.certificationId
          : undefined,
        // Certification snapshot - FAA 44807 Exemption (operating certification for this record)
        // Note: State applicator info is now captured per pilot on each drone flight
        certificationName: this.selectedCertification?.name || undefined,
        certificationNumber: this.selectedCertification?.certNumber || undefined,
        certificationState: undefined, // FAA certs are federal, not state-specific
        certificationExpiration: this.selectedCertification?.expirationDate || undefined,
        // Organization FAA certifications snapshot (for compliance reporting)
        orgFaa44807Number: this.orgFaa44807.number || undefined,
        orgFaa44807Expiration: this.orgFaa44807.expiration || undefined,
        orgFaaPart137Number: this.orgFaaPart137.number || undefined,
        orgFaaPart137Expiration: this.orgFaaPart137.expiration || undefined,
        chemicals,
        droneFlights,
        // Spray details
        whatWasTreated: formValue.whatWasTreated || undefined,
        targetPest: formValue.targetPest || undefined,
        nozzleDescription: formValue.nozzleDescription || undefined,
        nozzleToTargetDistance: formValue.nozzleToTargetDistance || undefined,
        pumpPressure: formValue.pumpPressure || undefined,
        travelSpeed: formValue.travelSpeed || undefined,
        driftManagementPractices: formValue.descriptionOfDriftManagementPractices || undefined,
        applicationTypes: this.getSelectedApplicationTypes(),
        applicationTypeOther: formValue.applicationTypeOther || undefined,
        totalVolume: formValue.totalVolume || undefined,
        tankMixNotes: formValue.tankMixNotes || undefined,
        // Weather
        weatherTemperature: formValue.temperature || undefined,
        weatherHumidity: formValue.humidity || undefined,
        weatherWindSpeed: formValue.windSpeed || undefined,
        weatherWindDirection: formValue.windDirection || undefined,
        weatherConditions: formValue.weatherConditions || undefined,
        // Incident information
        takeoffLandingDamage: formValue.takeOffOrLandingDamage || false,
        takeoffLandingDamageDescription: formValue.descriptionOfTakeOffOrLandingDamage || undefined,
        equipmentMalfunction: formValue.equipmentMalfunction || false,
        equipmentMalfunctionType: formValue.equipmentMalfunctionType || undefined,
        equipmentMalfunctionDescription: formValue.descriptionOfEquipmentMalfunction || undefined,
        hasAccident: formValue.hasAccident || false,
        accidentType: formValue.accidentType || undefined,
        accidentDescription: formValue.accidentDescription || undefined,
        uaLossType: formValue.uaLossType || undefined,
        // Safety & compliance
        reentryInterval: formValue.reentryInterval || undefined,
        ppeRequired: this.getSelectedPPE(),
        rinseEquipmentPostApplication: formValue.rinseEquipmentPostApplication || undefined,
        // Emergency contacts
        emergencyContactNumber: formValue.emergencyContactNumber || undefined,
        stateEmergencyContactNumber: formValue.stateEmergencyContactNumber || undefined,
        // Load/mix location — the Hub columns (use_field_for_load_mix,
        // load_mix_latitude/longitude) existed but nothing was ever sent, so
        // anything typed here was dropped on save.
        useFieldForLoadMix: formValue.useLocationForLoadMixLocation !== false,
        loadMixLatitude: formValue.useLocationForLoadMixLocation !== false
          ? undefined
          : (this.toNullableNumber(formValue.loadMixLocationLatitude) ?? undefined),
        loadMixLongitude: formValue.useLocationForLoadMixLocation !== false
          ? undefined
          : (this.toNullableNumber(formValue.loadMixLocationLongitude) ?? undefined),
        // Visual Observer & NOTAM
        visualObserver: formValue.visualObserver || undefined,
        voTested: formValue.voTested || false,
        notam: formValue.notam || undefined,
        // Checklist
        preFlightChecklist: formValue.preFlightChecklist || false,
        bufferZonesVerified: formValue.bufferZonesVerified || false,
        notes: formValue.notes || undefined
      };

      console.log('=== SPRAY RECORD SUBMISSION ===');
      console.log('Edit mode:', this.isEditMode);
      console.log('Record ID:', this.recordId);
      console.log('Submitting spray record DTO:', dto);
      console.log('Chemicals array length:', chemicals.length);
      console.log('Chemicals:', chemicals);
      console.log('Drone flights array length:', droneFlights.length);
      console.log('Drone flights:', droneFlights);
      console.log('==============================');

      let result;
      if (this.isEditMode && this.recordId) {
        result = await this.sprayRecordService.updateRecord(this.recordId, dto);
      } else {
        result = await this.sprayRecordService.createRecord(dto);
      }

      if (result) {
        console.log(this.isEditMode ? 'Spray record updated successfully' : 'Spray record created successfully');
        this.resetAfterSave();
        this.router.navigate(['/logbook/spray-records']);
      } else {
        console.error(this.isEditMode ? 'Failed to update spray record' : 'Failed to create spray record');
        this.genericError = (this.isEditMode ? 'Failed to update spray record' : 'Failed to create spray record');
      }
    } catch (error) {
      console.error('Error submitting spray record:', error);
      this.genericError = this.isEditMode ? 'Failed to update spray record' : 'Failed to create spray record';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Clear carry-over state after a successful save (spec G11).
   *
   * This route is reached repeatedly. Marking the FormGroup pristine is not
   * enough on its own: `droneFlights` and `appliedChemicals` are plain
   * component arrays, not form controls, so on any navigation where Angular
   * reuses the instance (edit → edit, which does not re-run `ngOnInit`) the
   * previous record's per-drone values would still be sitting there and would
   * be re-sent. That is exactly how a "phantom" flight count lands on a record
   * nobody touched — and unlike most stale-state bugs, this one writes a
   * number into a federal filing.
   */
  private resetAfterSave(): void {
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.droneFlights = [];
    this.appliedChemicals = [];
    this.totalChemicalUsage = [];
    this.availableDronesMap.clear();
  }

  onCancel(): void {
    this.router.navigate(['/logbook/spray-records']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }
}
